import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import Table from '../common/Table';
import RTFilters from '../common/RTFilters';
import RTFilteredTable from '../common/RTFilteredTable';
import AddEBChargeModal from './AddEBChargeModal';
import EBChargePreviewModal from './EBChargePreviewModal';
import { apiGet, apiPost } from '../../api/apiClient';
import apiClient from '../../api/apiClient';
import { toastSuccess, toastError } from '../common/toast';
import { withRoomGroupKey, EB_BILL_ROW_GROUP_CONFIG } from './ebBillTableUtils';
import { buildExportFileName } from '../../utils/dateHelper';
import { OCCUPANCY_GENDER_FILTER_OPTIONS } from '../../utils/occupancyConstants';
import '../common/rtCommon.css';
import './AddEBBillToInvoices.css';
import RTPageLayout from '../common/RTPageLayout';

const MONTH_OPTIONS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const ROOM_TYPE_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'AC', label: 'AC' },
  { value: 'Non AC', label: 'Non AC' },
];

const MISSING_EB_OPTIONS = [
  { value: '', label: 'All Invoices' },
  { value: 'yes', label: 'Without EB Charges Only' },
];

const getDefaultFilters = () => ({
  month: new Date().getMonth() + 1,
  year: new Date().getFullYear(),
  search: '',
  room_type: '',
  missing_eb_only: '',
  occupancy_gender: '',
});

const formatAmount = (amount) =>
  `₹${parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const AddEBBillToInvoices = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [flatRates, setFlatRates] = useState({ AC: null, 'Non AC': null });
  const [filters, setFilters] = useState(getDefaultFilters);
  const [appliedFilters, setAppliedFilters] = useState(getDefaultFilters);
  const [activeTab, setActiveTab] = useState('manual');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [previewRows, setPreviewRows] = useState([]);
  const [previewSkipped, setPreviewSkipped] = useState([]);
  const [previewErrors, setPreviewErrors] = useState([]);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewTitle, setPreviewTitle] = useState('Preview EB Charges');
  const [previewUploadFileName, setPreviewUploadFileName] = useState('');
  const [flatPreviewLoading, setFlatPreviewLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadingFileName, setUploadingFileName] = useState('');

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let y = currentYear - 2; y <= currentYear + 1; y++) {
      years.push({ value: y, label: y.toString() });
    }
    return years;
  }, []);

  const filterFields = useMemo(
    () => [
      {
        key: 'month',
        label: 'Month',
        type: 'dropdown',
        width: 'compact',
        options: MONTH_OPTIONS,
        applyImmediately: true,
      },
      {
        key: 'year',
        label: 'Year',
        type: 'dropdown',
        width: 'compact',
        options: yearOptions,
        applyImmediately: true,
      },
      {
        key: 'search',
        label: 'Search',
        type: 'text',
        width: 'wide',
        placeholder: 'PG name, mobile, or address...',
      },
      {
        key: 'room_type',
        label: 'Room Type',
        type: 'dropdown',
        width: 'compact',
        options: ROOM_TYPE_OPTIONS,
        applyImmediately: true,
      },
      {
        key: 'occupancy_gender',
        label: 'Gender',
        type: 'dropdown',
        width: 'compact',
        options: OCCUPANCY_GENDER_FILTER_OPTIONS,
        applyImmediately: true,
      },
      {
        key: 'missing_eb_only',
        label: 'EB Status',
        type: 'dropdown',
        width: 'medium',
        options: MISSING_EB_OPTIONS,
        applyImmediately: true,
      },
    ],
    [yearOptions],
  );

  const buildQueryParams = (filterParams) => {
    const params = new URLSearchParams({
      month: String(filterParams.month),
      year: String(filterParams.year),
    });
    if (filterParams.room_type) params.set('room_type', filterParams.room_type);
    if (filterParams.search?.trim())
      params.set('search', filterParams.search.trim());
    if (filterParams.missing_eb_only === 'yes')
      params.set('missing_eb_only', 'true');
    if (filterParams.occupancy_gender)
      params.set('occupancy_gender', filterParams.occupancy_gender);
    return params;
  };

  const loadInvoices = async (filterParams = appliedFilters) => {
    try {
      setLoading(true);
      const params = buildQueryParams(filterParams);
      const response = await apiGet(
        `/invoicing/eb-bill/invoices?${params.toString()}`,
      );
      if (response.data?.success) {
        setInvoices(response.data.invoices || []);
        setSummary(response.data.summary || null);
        setFlatRates(response.data.flat_rates || { AC: null, 'Non AC': null });
      } else {
        toastError('Failed to load invoices');
      }
    } catch (error) {
      console.error('Error loading EB bill invoices:', error);
      toastError('Error loading invoices');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInvoices(appliedFilters);
  }, []);

  const handleFilterChange = (key, value, { applyImmediately } = {}) => {
    const next = { ...filters, [key]: value ?? '' };
    setFilters(next);
    if (applyImmediately) {
      setAppliedFilters(next);
      loadInvoices(next);
    }
  };

  const handleApplyFilters = () => {
    setAppliedFilters(filters);
    loadInvoices(filters);
  };

  const handleClearFilters = () => {
    const defaults = getDefaultFilters();
    setFilters(defaults);
    setAppliedFilters(defaults);
    loadInvoices(defaults);
  };

  const appliedFilterPills = useMemo(() => {
    const pills = [];
    const monthLabel = MONTH_OPTIONS.find(
      (o) => o.value === appliedFilters.month,
    )?.label;
    if (monthLabel) pills.push({ key: 'month', label: `Month: ${monthLabel}` });
    if (appliedFilters.year)
      pills.push({ key: 'year', label: `Year: ${appliedFilters.year}` });
    if (appliedFilters.search?.trim()) {
      pills.push({
        key: 'search',
        label: `Search: ${appliedFilters.search.trim()}`,
      });
    }
    if (appliedFilters.room_type) {
      const rt = ROOM_TYPE_OPTIONS.find(
        (o) => o.value === appliedFilters.room_type,
      )?.label;
      pills.push({
        key: 'room_type',
        label: `Room: ${rt || appliedFilters.room_type}`,
      });
    }
    if (appliedFilters.missing_eb_only === 'yes') {
      pills.push({ key: 'missing_eb_only', label: 'Without EB only' });
    }
    if (appliedFilters.occupancy_gender) {
      const genderLabel =
        OCCUPANCY_GENDER_FILTER_OPTIONS.find(
          (o) => o.value === appliedFilters.occupancy_gender,
        )?.label || appliedFilters.occupancy_gender;
      pills.push({ key: 'occupancy_gender', label: `Gender: ${genderLabel}` });
    }
    return pills;
  }, [appliedFilters]);

  const handleRemoveAppliedFilter = (key) => {
    const next = { ...appliedFilters };
    if (key === 'month') next.month = new Date().getMonth() + 1;
    else if (key === 'year') next.year = new Date().getFullYear();
    else next[key] = '';
    setFilters(next);
    setAppliedFilters(next);
    loadInvoices(next);
  };

  const handleAddEbClick = (invoice) => {
    setSelectedInvoice(invoice);
    setShowAddModal(true);
  };

  const parseDownloadFilename = (response, fallbackBase) => {
    const header = response.headers?.['content-disposition'] || '';
    const match = header.match(/filename="?([^";\n]+)"?/i);
    if (match?.[1]) {
      return match[1].trim();
    }
    return buildExportFileName(fallbackBase, 'csv');
  };

  const handleDownloadTemplate = async () => {
    try {
      const params = buildQueryParams(appliedFilters);
      const response = await apiClient.get(
        `/invoicing/eb-bill/export-template?${params.toString()}`,
        { responseType: 'blob' },
      );
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const monthLabel =
        MONTH_OPTIONS.find((o) => o.value === appliedFilters.month)?.label ||
        appliedFilters.month;
      const invoiceCount = summary?.without_eb_charges ?? 0;
      link.download = parseDownloadFilename(
        response,
        `eb_bill_template_${monthLabel}_${appliedFilters.year}_${invoiceCount}_invoices`,
      );
      link.click();
      window.URL.revokeObjectURL(url);
      toastSuccess('Template downloaded');
    } catch (error) {
      console.error('Download error:', error);
      toastError('Failed to download template');
    }
  };

  const handleFlatRatePreview = async () => {
    if (!flatRates.AC && !flatRates['Non AC']) {
      toastError('Configure EB flat rates in Payment Settings first');
      return;
    }
    setFlatPreviewLoading(true);
    try {
      const response = await apiPost('/invoicing/eb-bill/flat-rate/preview', {
        month: appliedFilters.month,
        year: appliedFilters.year,
        room_type: appliedFilters.room_type || null,
        search: appliedFilters.search?.trim() || null,
        occupancy_gender: appliedFilters.occupancy_gender || null,
      });
      if (response.data?.success) {
        setPreviewRows(response.data.preview || []);
        setPreviewSkipped(response.data.skipped || []);
        setPreviewErrors([]);
        setPreviewUploadFileName('');
        setPreviewTitle('Flat Rate EB Charges Preview');
        setShowPreviewModal(true);
        if (!(response.data.preview || []).length) {
          toastError('No eligible invoices for flat rate addition');
        }
      }
    } catch (error) {
      toastError(error.response?.data?.detail || 'Failed to generate preview');
    } finally {
      setFlatPreviewLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadingFileName(file.name);
    setUploadLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('month', String(appliedFilters.month));
      formData.append('year', String(appliedFilters.year));
      const response = await apiClient.post(
        '/invoicing/eb-bill/upload/preview',
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        },
      );
      if (response.data?.success) {
        setPreviewRows(response.data.preview || []);
        setPreviewSkipped([]);
        setPreviewErrors(response.data.errors || []);
        setPreviewUploadFileName(file.name);
        setPreviewTitle('Upload EB Charges Preview');
        setShowPreviewModal(true);
        if (!(response.data.preview || []).length) {
          toastError('No valid rows found in uploaded file');
        }
      }
    } catch (error) {
      toastError(
        error.response?.data?.detail || 'Failed to parse uploaded file',
      );
    } finally {
      setUploadLoading(false);
      setUploadingFileName('');
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const tableData = useMemo(() => withRoomGroupKey(invoices), [invoices]);

  const tableColumns = useMemo(
    () => [
      { Header: 'Building', accessor: 'building_name' },
      {
        Header: 'Room',
        accessor: 'room_name',
        Cell: ({ value }) => value || 'N/A',
      },
      { Header: 'Invoice #', accessor: 'invoice_number' },
      { Header: 'PG Name', accessor: 'pg_name' },
      { Header: 'Mobile', accessor: 'mobile' },
      { Header: 'Room Type', accessor: 'room_type' },
      {
        Header: 'Current Amount',
        accessor: 'current_amount',
        Cell: ({ value }) => formatAmount(value),
      },
      {
        Header: 'EB Amount',
        accessor: 'eb_amount',
        Cell: ({ value, row }) =>
          row.has_eb_charges ? formatAmount(value) : '—',
      },
      {
        Header: 'Actions',
        accessor: 'invoice_id',
        Cell: ({ row }) => (
          <RTButton
            variant="outline-primary"
            size="sm"
            disabled={row.has_eb_charges}
            onClick={() => handleAddEbClick(row)}
          >
            Add EB Charge
          </RTButton>
        ),
      },
    ],
    [],
  );

  const hasFlatRates = flatRates.AC || flatRates['Non AC'];

  return (
    <RTPageLayout
      activePath={location.pathname}
      title="Add EB Bill to Monthly Rental Invoices"
    >
      <div className="rt-section" style={{ flex: 'none' }}>
        <RTFilters
          fields={filterFields}
          values={filters}
          onChange={handleFilterChange}
          onApply={handleApplyFilters}
          pills={appliedFilterPills}
          onRemovePill={handleRemoveAppliedFilter}
          onClearAll={handleClearFilters}
        />
      </div>

      {summary && (
        <div className="eb-bill-summary">
          <div className="eb-bill-summary-card">
            <div className="eb-bill-summary-label">Total Invoices</div>
            <div className="eb-bill-summary-value">
              {summary.total_invoices}
            </div>
          </div>
          <div className="eb-bill-summary-card">
            <div className="eb-bill-summary-label">With EB Charges</div>
            <div className="eb-bill-summary-value">
              {summary.with_eb_charges}
            </div>
          </div>
          <div className="eb-bill-summary-card">
            <div className="eb-bill-summary-label">Without EB Charges</div>
            <div className="eb-bill-summary-value">
              {summary.without_eb_charges}
            </div>
          </div>
        </div>
      )}

      <div className="rt-main-section">
        <div className="eb-bill-tabs">
          <button
            type="button"
            className={`eb-bill-tab ${activeTab === 'manual' ? 'active' : ''}`}
            onClick={() => setActiveTab('manual')}
          >
            Manual EB Addition
          </button>
          <button
            type="button"
            className={`eb-bill-tab ${activeTab === 'flat' ? 'active' : ''}`}
            onClick={() => setActiveTab('flat')}
          >
            Flat EB Bill Rate Addition
          </button>
          <button
            type="button"
            className={`eb-bill-tab ${activeTab === 'upload' ? 'active' : ''}`}
            onClick={() => setActiveTab('upload')}
          >
            Upload EB Invoice Sheet
          </button>
        </div>

        {activeTab === 'manual' && (
          <RTFilteredTable
            loading={loading}
            loadingMessage="Loading invoices..."
          >
            <Table
              columns={tableColumns}
              data={tableData}
              showSearch={false}
              showPagination
              exportable={false}
              disableSort
              rowGroupConfig={EB_BILL_ROW_GROUP_CONFIG}
              noDataText="No draft monthly rental invoices found for selected period"
            />
          </RTFilteredTable>
        )}

        {activeTab === 'flat' && (
          <div className="eb-bill-tab-panel">
            {flatPreviewLoading && (
              <div className="eb-bill-tab-loading">
                <RTSpinner message="Generating preview..." size={50} />
              </div>
            )}
            {hasFlatRates ? (
              <div className="eb-bill-flat-rates">
                <div>
                  <strong>AC flat rate:</strong>{' '}
                  {flatRates.AC ? formatAmount(flatRates.AC) : 'Not configured'}
                </div>
                <div>
                  <strong>Non AC flat rate:</strong>{' '}
                  {flatRates['Non AC']
                    ? formatAmount(flatRates['Non AC'])
                    : 'Not configured'}
                </div>
              </div>
            ) : (
              <p style={{ color: '#64748b', marginBottom: '1rem' }}>
                No flat EB rates configured. Set them in{' '}
                <RTButton
                  type="link"
                  className="rt-link-button"
                  onClick={() => navigate('/settings/payment-settings')}
                >
                  Payment Settings → EB Bill Rates
                </RTButton>
              </p>
            )}
            <RTButton
              variant="primary"
              onClick={handleFlatRatePreview}
              disabled={flatPreviewLoading || !hasFlatRates}
            >
              {flatPreviewLoading
                ? 'Loading preview...'
                : 'Add Flat Rate to Invoices'}
            </RTButton>
          </div>
        )}

        {activeTab === 'upload' && (
          <div className="eb-bill-tab-panel">
            {uploadLoading && (
              <div className="eb-bill-tab-loading">
                <RTSpinner
                  message={
                    uploadingFileName
                      ? `Uploading and parsing ${uploadingFileName}...`
                      : 'Uploading and parsing file...'
                  }
                  size={50}
                />
              </div>
            )}
            <div className="eb-bill-upload-row">
              <RTButton
                variant="outline-primary"
                onClick={handleDownloadTemplate}
              >
                Download EB Sheet Format (CSV)
              </RTButton>
              <RTButton
                variant="primary"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadLoading}
              >
                {uploadLoading ? 'Uploading...' : 'Upload CSV / XLSX'}
              </RTButton>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx"
                style={{ display: 'none' }}
                onChange={handleFileUpload}
              />
            </div>
            <p style={{ color: '#64748b', fontSize: '0.9rem' }}>
              Download the template for invoices without EB charges, fill in the
              eb_amount column, then upload.
            </p>
          </div>
        )}
      </div>

      <AddEBChargeModal
        isOpen={showAddModal}
        invoice={selectedInvoice}
        onClose={() => {
          setShowAddModal(false);
          setSelectedInvoice(null);
        }}
        onSaved={() => loadInvoices(appliedFilters)}
      />

      <EBChargePreviewModal
        isOpen={showPreviewModal}
        title={previewTitle}
        previewRows={previewRows}
        skipped={previewSkipped}
        errors={previewErrors}
        onClose={() => {
          setShowPreviewModal(false);
          setPreviewUploadFileName('');
        }}
        onSaved={() => loadInvoices(appliedFilters)}
        uploadFileName={previewUploadFileName}
      />
    </RTPageLayout>
  );
};

export default AddEBBillToInvoices;
